import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { humintErrorCode, isAbort } from '@/features/humint/mappers/errors'
import { AUDITED_READ } from '@/features/humint/mappers/subject-page'
import { recordingVoiceKeys } from '@/features/recording-voices/api'
import { voiceLeadKeys } from '@/features/voice-leads/hooks/useVoiceLeadCount'
import { ApiError } from '@/services/api/client'
import {
  type RecordingVoiceMatch,
  recordingVoiceMatchesApi,
} from '@/services/api/recording-voice-matches'

export interface RecordingMatchContext {
  caseId: string
  subjectRef: string
  matchId: string
}

export function recordingMatchProblem(error: unknown): string | null {
  if (!error || isAbort(error)) return null
  if (humintErrorCode(error) === 'audit_unavailable')
    return 'humint.tattooMatch.change.problem.audit_unavailable'
  if (error instanceof ApiError && error.status === 403)
    return 'humint.tattooMatch.change.problem.forbidden'
  if (error instanceof ApiError && error.status === 404)
    return 'humint.voice.match.change.problem.not_found'
  if (error instanceof ApiError && error.status === 409)
    return 'identity.detail.audio.match.changed'
  return 'identity.detail.audio.match.decisionFailed'
}

const matchKey = ({ caseId, subjectRef, matchId }: RecordingMatchContext) => [
  'case',
  caseId,
  'identity-audio',
  subjectRef,
  'match',
  matchId,
]

export function recordingMatchQuery(context: RecordingMatchContext) {
  const { caseId, subjectRef, matchId } = context
  return {
    queryKey: matchKey(context),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      recordingVoiceMatchesApi.read(caseId, subjectRef, matchId, signal),
    ...AUDITED_READ,
  }
}

async function refreshRecordingReviews(client: QueryClient, caseId: string) {
  await Promise.all([
    client.invalidateQueries({
      queryKey: ['case', caseId, 'identity-audio'],
      predicate: query => query.queryKey.length === 4,
    }),
    client.invalidateQueries({ queryKey: voiceLeadKeys.recordings(caseId), exact: true }),
    client.invalidateQueries({ queryKey: voiceLeadKeys.count(caseId), exact: true }),
    client.invalidateQueries({ queryKey: recordingVoiceKeys.pairs(caseId), exact: true }),
    client.invalidateQueries({ queryKey: ['recording-voices', caseId] }),
  ])
}

export function recordingMatchMutation(client: QueryClient, context: RecordingMatchContext) {
  const { caseId, subjectRef, matchId } = context
  return {
    mutationFn: (decision: 'same' | 'different') =>
      recordingVoiceMatchesApi.decide(caseId, subjectRef, matchId, decision),
    onSuccess: async (state: Awaited<ReturnType<typeof recordingVoiceMatchesApi.decide>>) => {
      client.setQueryData<RecordingVoiceMatch>(
        matchKey(context),
        current => current && { ...current, status: state.status }
      )
      await refreshRecordingReviews(client, caseId)
    },
    onError: async (error: unknown) => {
      if (!(error instanceof ApiError) || ![404, 409].includes(error.status)) return
      await refreshRecordingReviews(client, caseId)
      await client.refetchQueries({ queryKey: matchKey(context), exact: true, type: 'active' })
    },
  }
}

export function useRecordingMatch(context: RecordingMatchContext) {
  const client = useQueryClient()
  const query = useQuery(recordingMatchQuery(context))
  const mutation = useMutation(recordingMatchMutation(client, context))
  return {
    query,
    actions: {
      busy: mutation.isPending,
      problem: recordingMatchProblem(mutation.error),
      onDecide: mutation.mutate,
    },
  }
}
